import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { documentImportApi } from '../../api/documentImport';
import { citationShortId, resolveImportChunkTarget } from './importCitation';
import SourceViewer from './SourceViewer';

const SourceViewerContext = createContext(null);

const IDLE = {
  openImportChunkCitation: async () => false,
  openImportSourceByChunkId: async () => false,
  closeSourceViewer: () => {},
};

export function useSourceViewer() {
  return useContext(SourceViewerContext) || IDLE;
}

/**
 * App-level overlay so a citation click from Chat / Dossier / Board
 * lands here and Back returns to that same place. Not a new route.
 */
export function SourceViewerProvider({ children }) {
  const [source, setSource] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const closeSourceViewer = useCallback(() => {
    setOpen(false);
    setSource(null);
    setError(null);
  }, []);

  const openImportChunkCitation = useCallback(async (shortId, citationData) => {
    const target = resolveImportChunkTarget(shortId, citationData);
    if (!target) return false;
    try {
      const payload = await documentImportApi.getSourceByChunk(target.chunkId);
      setSource(payload);
      setError(null);
      setOpen(true);
      return true;
    } catch (err) {
      if (!target.explicit && err.status === 404) {
        return false;
      }
      setSource(null);
      setError(err.message || 'Could not open the imported source.');
      setOpen(true);
      return true;
    }
  }, []);

  const openImportSourceByChunkId = useCallback(async (chunkId) => {
    if (!chunkId) return false;
    return openImportChunkCitation(citationShortId(chunkId), {
      id: chunkId,
      kind: 'import_chunk',
    });
  }, [openImportChunkCitation]);

  const value = useMemo(() => ({
    openImportChunkCitation,
    openImportSourceByChunkId,
    closeSourceViewer,
  }), [openImportChunkCitation, openImportSourceByChunkId, closeSourceViewer]);

  return (
    <SourceViewerContext.Provider value={value}>
      {children}
      {open && (
        <SourceViewer
          source={source}
          error={error}
          onBack={closeSourceViewer}
        />
      )}
    </SourceViewerContext.Provider>
  );
}
